import React from "react";
import { ClipboardIcon, CheckIcon } from "@heroicons/react/24/outline";

const EXPLANATION_STYLE = "border border-border bg-muted/50";

const MAX_LABEL_LEN = 50;

/**
 * Parses an explanation line into optional label and content.
 * If the line has "Label: content" format within the first MAX_LABEL_LEN chars, splits it.
 */
function parseExplanationLine(line: string): { label: string | null; content: string } {
  const cleaned = line.replace(/^[-•]\s*/, "");
  const colonIdx = cleaned.indexOf(": ");
  const [label, content] =
    colonIdx >= 0 && colonIdx <= MAX_LABEL_LEN
      ? [cleaned.slice(0, colonIdx), cleaned.slice(colonIdx + 2)]
      : [null, cleaned];
  return { label, content };
}

interface EvaluationExplanationProps {
  explanation: string;
  evaluationId: string;
  copiedId: string | null;
  onCopy: (id: string) => void;
}

/**
 * Renders evaluation explanation text with optional label/content parsing.
 * Splits on newlines, strips bullet prefixes, and optionally extracts "Label: content" pairs.
 */
export function EvaluationExplanation({
  explanation,
  evaluationId,
  copiedId,
  onCopy,
}: EvaluationExplanationProps) {
  const lines = explanation
    .split(/\n+/)
    .filter((line) => line.trim())
    .map(parseExplanationLine);

  return (
    <div className={`mt-2 pt-2 rounded-md p-3 ${EXPLANATION_STYLE}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Explanation
        </h4>
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(explanation);
            onCopy(evaluationId);
          }}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          title="Copy explanation"
        >
          {copiedId === evaluationId ? (
            <CheckIcon className="w-3.5 h-3.5 text-success" />
          ) : (
            <ClipboardIcon className="w-3.5 h-3.5" />
          )}
          {copiedId === evaluationId ? "Copied" : "Copy"}
        </button>
      </div>
      <div className="space-y-3">
        {lines.map(({ label, content }, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            {label ? (
              <span className="text-sm font-semibold text-foreground">{label}</span>
            ) : null}
            <span className="text-sm text-foreground whitespace-pre-wrap pl-0.5">
              {content}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
